import { useContext, useState } from 'react'
import Face4OutlinedIcon from '@mui/icons-material/Face4Outlined'
import { DeccaContext } from '../../StateManagment/DeccaContext.js'
import { Auth } from '../../StateManagment/Auth'
import newConversation from '../../agents/newConversation.js'
import { db, timestamp } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { useFirestoreGeneralTwo, useFirestoreOrderBy } from '../../firebase/useFirestore'
import uuid from 'react-uuid'
import DeepAnalysisSectionHeader from './DeepAnalysisSectionHeader'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import periodsForAI from '../../helpers/compareHelpers/periodsForAI'

/**
 * Open-question AI section on a research deep-analysis page: the user picks an
 * open (paragraph) question of the anchor research and Decca analyses the free-text
 * answers. Mirrors the old AIOpenQuestionAnalysis Decca wiring (subject
 * 'OpenQuestionAnalysis', same contextData) so the existing Cloud Function
 * context provider keeps working.
 *
 * @param {object} props
 * @param {object} props.anchor - The anchor Research doc
 */
const OpenQuestionAISection = ({ anchor }) => {
    const [auth] = useContext(Auth)
    const {
        chatOpen,
        setChatOpen,
        setSelectedConversationID,
        setShowConversations,
        setSubject,
        setContextData,
    } = useContext(DeccaContext)

    const [questionFieldId, setQuestionFieldId] = useState('')
    const [question, setQuestion] = useState('')
    // The page period (contract in functions/utils/parseAnalysisPeriods.js).
    // AI-CONTEXT: See docs/features/deep-analysis-period.md
    const periods = periodsForAI(useContext(DeepAnalysisPeriodContext))

    const openQuestions = useFirestoreGeneralTwo(
        'QuestionnaireFields',
        'QuestionnaireID',
        anchor.QuestionnaireID || '',
        'Type',
        'paragraph'
    )
    const conversations = useFirestoreOrderBy('Conversations', 'Position', 'desc')

    const selectQuestion = (e) => {
        const option = e.target.options[e.target.selectedIndex]
        setQuestionFieldId(option.value)
        setQuestion(option.value ? option.innerText : '')
    }

    const startAnalysis = async () => {
        if (!questionFieldId) return
        const conversationID = uuid()
        const title = `Analyse van "${question}"`
        const message = `<h2>Hallo ${auth.ForName}</h2><p>Zal ik een algemene analyse doen op de vraag "${question}" of wil je iets specifiekers weten?</p>`

        setSubject('OpenQuestionAnalysis')
        setContextData({
            questionnaireId: anchor.QuestionnaireID,
            questionFieldId,
            question,
            researchId: anchor.ID,
            ...(periods ? { periods } : {}),
        })

        setChatOpen(chatOpen === false ? true : true)
        await newConversation(
            conversationID,
            title,
            message,
            conversations.length + 1,
            client,
            timestamp,
            uuid(),
            auth.ID,
            auth.ForName,
            db
        )
        setSelectedConversationID(conversationID)
        setShowConversations(false)
    }

    return (
        <div className='deep-analysis-section'>
            <DeepAnalysisSectionHeader
                kicker='Verdiepen'
                title='Open vraag analyseren'
                description={`Laat Decca de open antwoorden op een vraag uit "${anchor.Title}" analyseren en stel vervolgvragen in het gesprek.`}
            />
            {Array.isArray(openQuestions) && openQuestions.length === 0 ? (
                <p className='deep-analysis-empty'>
                    Dit onderzoek heeft geen open vragen om te analyseren.
                </p>
            ) : (
                <>
                    <select className='deep-analysis-indicator-select' onChange={selectQuestion} value={questionFieldId}>
                        <option value=''>-- Selecteer open vraag --</option>
                        {Array.isArray(openQuestions) && openQuestions.map((q) => (
                            <option key={q.ID} value={q.ID}>{q.Question}</option>
                        ))}
                    </select>
                    {questionFieldId && (
                        <div id='page-header-impact-ai-container' className='analysis-ai-container' onClick={startAnalysis}>
                            <div id='page-header-impact-ai-inner-container'>
                                <Face4OutlinedIcon />
                                <p>Analyseer met Decca</p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default OpenQuestionAISection
